import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, X, Copy, RefreshCw, Trash2, Loader2, UserPlus } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AppShell from "../components/app/AppShell";

function Field({ label, value, onChange, placeholder, type = "text", testId, autoCapitalize }) {
  return (
    <div className="mb-4">
      <label className="label-up">{label}</label>
      <input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoCapitalize={autoCapitalize} className="input-field" />
    </div>
  );
}

export default function ManageAgents() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agency, setAgency] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => { if (user && user.role !== "admin") navigate("/dashboard", { replace: true }); }, [user, navigate]);

  const load = useCallback(async () => {
    try { setItems(await api("/agents")); }
    catch (e) { alert(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reset = () => { setName(""); setEmail(""); setPassword(""); setAgency(""); setPhone(""); };

  const create = async () => {
    if (!name.trim() || !email.trim() || !password) { alert("Name, email, and password are required."); return; }
    if (password.length < 6) { alert("Password must be at least 6 characters."); return; }
    setCreating(true);
    try {
      await api("/agents", { method: "POST", body: { name: name.trim(), email: email.trim(), password, agency: agency.trim(), phone: phone.trim() } });
      reset(); setShowAdd(false); await load();
    } catch (e) { alert(e.message); }
    finally { setCreating(false); }
  };

  const copyCode = (code) => { if (!code) return; navigator.clipboard.writeText(code); alert("Invite code copied!"); };
  const removeAgent = async (a) => {
    const text = a.partner_count > 0
      ? `Delete ${a.name}? This agent has ${a.partner_count} partner(s) and ${a.referral_count} referral(s) — they will all be deleted too.`
      : `Delete ${a.name}? This cannot be undone.`;
    if (!window.confirm(text)) return;
    try { const cascade = a.partner_count > 0 ? "?cascade=true" : ""; await api(`/agents/${a.id}${cascade}`, { method: "DELETE" }); await load(); }
    catch (e) { alert(e.message); }
  };
  const regenerateCode = async (a) => {
    try { await api(`/agents/${a.id}/regenerate-invite-code`, { method: "POST" }); await load(); }
    catch (e) { alert(e.message); }
  };

  return (
    <AppShell>
      <div className="px-5 lg:px-10 py-8 max-w-4xl">
        <div className="flex items-center justify-between gap-3">
          <button onClick={() => navigate(-1)} className="w-10 h-10 grid place-items-center -ml-2 rounded-lg hover:bg-[#F5F5F5]"><ArrowLeft size={20} /></button>
          <button data-testid="add-agent-btn" onClick={() => setShowAdd(true)} className="w-10 h-10 rounded-full grid place-items-center text-white" style={{ background: "#D62828" }}><Plus size={18} /></button>
        </div>

        <div className="mt-4">
          <h1 className="font-display text-3xl sm:text-4xl font-black">Manage Agents</h1>
          <p className="text-sm mt-1" style={{ color: "#5A5A5A" }}>Downline agents in your agency</p>
        </div>

        {loading ? (
          <div className="grid place-items-center py-24"><Loader2 className="animate-spin" color="#D62828" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <UserPlus size={48} color="#8A8A8A" className="mx-auto" />
            <h3 className="font-display text-xl font-black mt-3">No agents yet</h3>
            <p className="text-sm" style={{ color: "#5A5A5A" }}>Add agents in your downline so they can manage their own referral partners.</p>
            <button onClick={() => setShowAdd(true)} className="btn-primary mt-5"><Plus size={16} /> Add Your First Agent</button>
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            {items.map((a) => (
              <div key={a.id} className="bg-white rounded-2xl p-4 border" style={{ borderColor: "#E8E8E8" }}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-extrabold">{a.name}</div>
                    <div className="text-xs" style={{ color: "#5A5A5A" }}>{a.email}</div>
                    {a.agency && <div className="text-[11px]" style={{ color: "#8A8A8A" }}>{a.agency}</div>}
                  </div>
                  <div className="text-center px-2">
                    <div className="font-display text-lg font-black tnum" style={{ color: "#D62828" }}>{a.partner_count}</div>
                    <div className="text-[9px] font-extrabold tracking-wider" style={{ color: "#8A8A8A" }}>PARTNERS</div>
                  </div>
                  <div className="text-center px-2">
                    <div className="font-display text-lg font-black tnum" style={{ color: "#D62828" }}>{a.referral_count}</div>
                    <div className="text-[9px] font-extrabold tracking-wider" style={{ color: "#8A8A8A" }}>REFERRALS</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t" style={{ borderColor: "#E8E8E8" }}>
                  <span className="text-[9px] font-extrabold tracking-wider" style={{ color: "#8A8A8A" }}>INVITE CODE</span>
                  <button onClick={() => copyCode(a.invite_code)} className="flex-1 flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: "#E3F0FB" }}>
                    <span className="font-display font-black flex-1 text-left tracking-widest" style={{ color: "#D62828" }}>{a.invite_code || "—"}</span>
                    <Copy size={12} color="#D62828" />
                  </button>
                  <button data-testid={`regen-${a.id}`} onClick={() => regenerateCode(a)} className="w-8 h-8 grid place-items-center rounded hover:bg-[#F5F5F5]"><RefreshCw size={14} color="#5A5A5A" /></button>
                  <button data-testid={`del-${a.id}`} onClick={() => removeAgent(a)} className="w-8 h-8 grid place-items-center rounded hover:bg-[#FFEBEE]"><Trash2 size={14} color="#C62828" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex" onClick={() => { reset(); setShowAdd(false); }}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="ml-auto relative w-full max-w-md h-full bg-white overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between" style={{ borderColor: "#E8E8E8" }}>
              <h2 className="font-display text-xl font-black">Add Downline Agent</h2>
              <button onClick={() => { reset(); setShowAdd(false); }}><X size={22} /></button>
            </div>
            <div className="p-5">
              <p className="text-xs italic mb-4" style={{ color: "#5A5A5A" }}>This agent will have their own dashboard, referral partners, and compensation grid (copied from yours by default).</p>
              <Field label="Name" value={name} onChange={setName} placeholder="Tony Smith" testId="agent-name" />
              <Field label="Agency / Team" value={agency} onChange={setAgency} placeholder="Smith Insurance Group" testId="agent-agency" />
              <Field label="Phone" value={phone} onChange={setPhone} placeholder="(555) 123-4567" type="tel" testId="agent-phone" />
              <Field label="Email (login)" value={email} onChange={setEmail} placeholder="tony@example.com" type="email" testId="agent-email" />
              <Field label="Initial Password" value={password} onChange={setPassword} placeholder="Min 6 characters" type="password" testId="agent-password" />
              <button data-testid="agent-submit" onClick={create} disabled={creating} className="btn-primary w-full !py-3.5 mt-3">
                {creating ? <Loader2 className="animate-spin" size={16} /> : (<><UserPlus size={16} /> Create Agent</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
